import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import type {
  BaseServiceOptions,
  Pagination,
  TImportFile,
  TRecordAction,
} from '@/core/types'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import {
  TCatalogs,
  type TProject,
  type TProjects,
  TStatuses,
  moduleServiceHandlers,
  projectStatusService,
  projectsTypesService,
  serviceConfig,
} from '../../..'

const { model, projectStatus, projectTypes } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, handlers, enableQuery, filters, modelId } = options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TProject>({
    model,
    currentPage: currentPage ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const deleteRecord = useMutation<void, AxiosError, TProject>(
    (payload) => moduleServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, project) => serviceHandlers.deleteRecord(project),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecords = useMutation<void, AxiosError, Array<number>>(
    (payload) => moduleServiceHandlers.deleteRecords({ ids: payload }),
    {
      onSuccess: (_, ids) => serviceHandlers.deleteRecords(ids),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TProject, AxiosError, TProject>(
    (payload: TProject) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TProject, AxiosError, TProject>(
    (payload: TProject) => moduleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TProject, AxiosError, TRecordAction>(
    (payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const importRecords = useMutation<void, AxiosError, TImportFile>(
    (payload: TImportFile) => {
      const formData = new FormData()
      formData.append('importFile', payload.file)
      return moduleServiceHandlers.importRecords(formData)
    },
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TProjects, AxiosError>(
    [model, currentPage, filters?.loadRecords],
    () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (data) => {
        if (data.meta) {
          handlers?.onLoadRecords?.(data.meta ?? defaultPagination)
        }
      },
    },
  )

  const loadRecord = useQuery<TProject, AxiosError>(
    [model, modelId],
    ({ queryKey }) =>
      moduleServiceHandlers.loadRecord({
        id: queryKey[1] as number,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecord,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (payload) => {
        options.handlers?.onLoadRecord?.(payload)
      },
      select: (payload) => ({
        ...payload,
        status_id: payload.status?.id,
      }),
    },
  )

  const statusCatalog = useQuery<TStatuses, AxiosError>(
    [projectStatus.model, projectStatus.version],
    () => projectStatusService.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.statusCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const projectTypesCatalog = useQuery<TCatalogs, AxiosError>(
    [projectTypes.model, projectTypes.version],
    () => projectsTypesService.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.projectTypesCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  return {
    loadRecords,
    addRecord,
    editRecord,
    deleteRecords,
    deleteRecord,
    importRecords,
    actionRecord,
    loadRecord,
    statusCatalog,
    projectTypesCatalog,
  }
}
